/* ChainFlow OS — operational notification dispatcher.
   --------------------------------------------------------------------
   • Honours user preferences (cf:notif-prefs) and per-deal mutes
     (cf:notif-muted-deals).
   • Fires native browser notifications only when the tab is hidden, so
     in-app sonner toasts and the in-page bell are not duplicated.
   • Notifications deep-link directly into the relevant deal room.
   • Categories: reply, document, risk, milestone, overdue.
   • If the SW is active we hand off to ServiceWorkerRegistration.show
     so the OS handles delivery (lock-screen friendly on Android/PWA).
*/

const PREFS_KEY        = "cf:notif-prefs";
const MUTED_DEALS_KEY  = "cf:notif-muted-deals";
const MUTE_OVERRIDES_KEY = "cf:notif-mute-overrides"; // { [dealId]: { critical: true } }
const PERMISSION_ASKED = "cf:notif-permission-asked-at";

// Categories that bypass mute when the per-deal "critical alerts override"
// is enabled. Examples: exchange failed, mortgage expiry, AML issue,
// completion risk escalated, deal at risk of collapse.
const CRITICAL_CATEGORIES = new Set(["risk", "overdue"]);

const DEFAULTS = {
  emailDigest: true,
  pushAlerts: true,
  riskAlerts: true,
  milestoneUpdates: true,
  mentionAlerts: true,
};

const getPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return { ...DEFAULTS, ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return DEFAULTS;
  }
};

export const isDealMuted = (dealId) => {
  if (!dealId) return false;
  try {
    const raw = localStorage.getItem(MUTED_DEALS_KEY);
    const set = raw ? JSON.parse(raw) : [];
    return Array.isArray(set) && set.includes(dealId);
  } catch {
    return false;
  }
};

export const getMutedDeals = () => {
  try {
    const raw = localStorage.getItem(MUTED_DEALS_KEY);
    const set = raw ? JSON.parse(raw) : [];
    return Array.isArray(set) ? set : [];
  } catch {
    return [];
  }
};

export const muteDeal = (dealId) => {
  if (!dealId) return;
  const set = new Set(getMutedDeals());
  set.add(dealId);
  try { localStorage.setItem(MUTED_DEALS_KEY, JSON.stringify(Array.from(set))); } catch { /* noop */ }
  window.dispatchEvent(new CustomEvent("cf:muted-changed"));
};

export const unmuteDeal = (dealId) => {
  if (!dealId) return;
  const set = new Set(getMutedDeals());
  set.delete(dealId);
  try { localStorage.setItem(MUTED_DEALS_KEY, JSON.stringify(Array.from(set))); } catch { /* noop */ }
  window.dispatchEvent(new CustomEvent("cf:muted-changed"));
};

/* Per-deal mute overrides (e.g. allow critical risks through). */
const readOverrides = () => {
  try {
    const raw = localStorage.getItem(MUTE_OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const writeOverrides = (obj) => {
  try { localStorage.setItem(MUTE_OVERRIDES_KEY, JSON.stringify(obj || {})); } catch { /* noop */ }
};

export const getMuteOverride = (dealId) => {
  if (!dealId) return { critical: false };
  const all = readOverrides();
  return { critical: false, ...(all[dealId] || {}) };
};

export const setMuteOverride = (dealId, patch) => {
  if (!dealId) return;
  const all = readOverrides();
  all[dealId] = { ...(all[dealId] || {}), ...(patch || {}) };
  writeOverrides(all);
  window.dispatchEvent(new CustomEvent("cf:muted-changed"));
};

const CATEGORY_PREF_KEY = {
  reply:     "pushAlerts",      // operational replies
  document:  "pushAlerts",
  risk:      "riskAlerts",
  milestone: "milestoneUpdates",
  overdue:   "riskAlerts",
  mention:   "mentionAlerts",
};

export const wantsCategory = (category) => {
  const prefs = getPrefs();
  if (!prefs.pushAlerts) return false;
  const k = CATEGORY_PREF_KEY[category];
  if (!k) return true; // unknown category → only blocked by master push toggle
  return Boolean(prefs[k]);
};

/* ─── Operational discipline gate ─────────────────────────────────────────
 *
 * ChainFlow's notification philosophy: only interrupt the agent when one
 * of five specific operational reasons applies:
 *
 *   1. momentum-at-risk   — risk flagged on a live deal
 *   2. response-overdue   — counterparty has not responded inside the
 *                            agreed SLA window (overdue / chase)
 *   3. blocked            — a milestone or stage transition cannot
 *                            proceed without intervention
 *   4. milestone-completed — a stage gate is genuinely cleared
 *   5. action-required    — the current user has been @-mentioned or
 *                            assigned a task that needs their hand
 *
 * Everything else (info pings, FYIs, "saved" toasts, generic activity)
 * is intentionally silent. The in-page notification panel + sidebar bell
 * still surface them; we just don't fire the OS / native channel.
 *
 * Map of category → operational reason. Anything mapped to `null` is
 * deliberately not allowed to fire an interruption.
 */
const OPERATIONAL_REASONS = {
  risk:      "momentum-at-risk",
  overdue:   "response-overdue",
  blocked:   "blocked",
  milestone: "milestone-completed",
  mention:   "action-required",
  // Below: surfaced in-app only, NEVER interrupt.
  reply:     null,
  document:  null,
  info:      null,
  fyi:       null,
};

/** Returns the operational reason or `null` if this category is silent. */
export const operationalReasonFor = (category) => OPERATIONAL_REASONS[category] ?? null;

/**
 * shouldNotify({ category, dealId, deal })
 *
 * Single source of truth for "should this become a native interruption?".
 * Combines (in order):
 *   1. operational discipline allowlist (only the 5 reasons above)
 *   2. user category preferences
 *   3. per-deal mute state (with critical-override escape hatch)
 *   4. tab visibility (no native push if the user is already looking)
 */
export const shouldNotify = ({ category = "reply", dealId } = {}) => {
  // 1. Operational discipline allowlist
  if (operationalReasonFor(category) === null) return false;
  // 2. User category preference
  if (!wantsCategory(category)) return false;
  // 3. Per-deal mute (allow critical-override to escape)
  if (isDealMuted(dealId)) {
    const overrides = getMuteOverride(dealId);
    const isCritical = CRITICAL_CATEGORIES.has(category);
    if (!(overrides.critical && isCritical)) return false;
  }
  // 4. In-page already → in-app surface handles it
  if (typeof document !== "undefined" && document.visibilityState === "visible") return false;
  return true;
};

export const canShowNative = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  Notification.permission === "granted";

export const wasPermissionAsked = () => Boolean(localStorage.getItem(PERMISSION_ASKED));
export const markPermissionAsked = () => {
  try { localStorage.setItem(PERMISSION_ASKED, String(Date.now())); } catch { /* noop */ }
};

/* notify({ title, body, dealId, category, url }) -------------------- */
export const notify = async ({ title, body, dealId, category = "reply", url }) => {
  if (!title) return;
  if (!canShowNative()) return;
  // ALL discipline + preference + mute + visibility gating lives in one
  // place so we cannot accidentally bypass it from a caller.
  if (!shouldNotify({ category, dealId })) return;

  const target = url || (dealId ? `/deal-rooms/${dealId}` : "/notifications");
  const opts = {
    body,
    icon: "/icons/icon-192.png",
    badge: "/icons/icon-192.png",
    tag: dealId ? `cf-deal-${dealId}-${category}` : `cf-${category}`,
    renotify: false,
    silent: category !== "risk" && category !== "overdue",
    data: { url: target, category, dealId: dealId || null, ts: Date.now() },
  };

  // Prefer service-worker delivery on supported platforms (better mobile + lock-screen)
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.showNotification) {
        await reg.showNotification(title, opts);
        return;
      }
    } catch {
      /* fall through to direct Notification */
    }
  }

  try {
    const n = new Notification(title, opts);
    n.onclick = (event) => {
      event.preventDefault();
      window.focus();
      window.location.href = target;
      n.close();
    };
  } catch {
    /* noop — quota or denied at the platform level */
  }
};

/* requestNotificationPermission — returns the resulting permission */
export const requestNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  markPermissionAsked();
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return "default";
  }
};
